import { CardTitle, BodyText } from '@/components/ui/Typography';
import { URLS } from '@/lib/constants';
import { LOADING_SPINNER_COLOR } from '@/lib/loadingSpinnerColor';
import { logger } from '@/lib/logger';
import { fetchCommunityAutologinUrl } from '@/services/communityAutologin.service';
import { useAuth } from '@/lib/AuthContext';
import { readMembershipSource } from '@/lib/membershipSource';
import { getCustomerInfo, hasActiveEntitlement } from '@/lib/purchases/RevenueCat';
import { appRouteForAbsoluteUrl } from '@/lib/resolveAppLink';
import { parseWpCourseUrl, resolveWpCourseRoute } from '@/lib/wpCourseLinks';
import { getCourseBySlug } from '@/supabase/services/courses';
import { publishedLessonExists } from '@/supabase/services/lesson';
import { openWpUrlInBrowserLoggedIn } from '@/lib/wpBrowserAutologin';
import { Ionicons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Linking, Modal, Platform, Pressable, TouchableOpacity, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { WebView } from 'react-native-webview';
import { XStack, YStack } from 'tamagui';

const BG = '#191b1f'; // matches the community page background
const HEADER_BG = '#0D0D0D';

// Pro-upgrade entry paths: /go-pro (stable alias, os-sessions CTA) and
// /switch-subscription (WooCommerce's switch entry, used by pro CTAs authored
// inside Circle content). Navigations here route by billing instead of loading.
const PRO_UPGRADE_PATH_RE = /^\/(go-pro|switch-subscription)(\/|$)/;

type Phase = 'loading' | 'ready' | 'not_member' | 'error';

/**
 * Hides the WordPress site chrome (Kadence header/footer, admin bar) when the
 * community page renders inside the app WebView — the native modal already has
 * its own "The Hang" header. Scoped to the WP host only, so Circle top-frame
 * hops (pro.openstudiojazz.com, *.circle.so) are untouched; the injection only
 * runs in the main frame, never inside the Circle iframe.
 *
 * The theme's own sizing JS computes the embed height as
 * window height − .site-header height at DOMContentLoaded; injecting the CSS
 * before content loads means the hidden header measures 0 and the community
 * iframe fills the screen with no further work.
 */
const HIDE_WP_CHROME_JS = `
(function () {
  try {
    var h = location.hostname.toLowerCase();
    if (h !== 'www.openstudiojazz.com' && h !== 'openstudiojazz.com') return;
    var css = '#masthead,#mobile-header,#colophon,#wpadminbar{display:none!important}' +
      'html{margin-top:0!important}body{padding-top:0!important}';
    var style = document.createElement('style');
    style.id = 'osj-app-hide-chrome';
    style.appendChild(document.createTextNode(css));
    var attach = function () {
      if (!document.getElementById('osj-app-hide-chrome')) {
        (document.head || document.documentElement).appendChild(style);
      }
    };
    attach();
    document.addEventListener('DOMContentLoaded', attach);
  } catch (e) {}
})();
true;
`;

interface CommunityWebViewModalProps {
  /** Community-relative path to open (e.g. "/community/c/workshops/"); null hides the modal. */
  path: string | null;
  onClose: () => void;
}

function hostnameOf(url: string): string {
  try {
    return new URL(url).hostname.toLowerCase();
  } catch {
    return '';
  }
}

function wpHost(): string {
  return hostnameOf(URLS.WORDPRESS_BASE) || 'www.openstudiojazz.com';
}

/** Hosts allowed to load as the WebView's top frame. */
function isAllowedTopFrameHost(host: string): boolean {
  if (!host) return false;
  if (host === wpHost() || host === 'www.openstudiojazz.com' || host === 'openstudiojazz.com') return true;
  // Circle: custom domain + *.circle.so (sandbox, OAuth hops).
  if (host === 'pro.openstudiojazz.com') return true;
  if (host === 'circle.so' || host.endsWith('.circle.so')) return true;
  return false;
}

/**
 * The Hang, in-app (native only). Loads a one-time WP autologin URL so the
 * WebView carries a WordPress session; the embedded Circle community then
 * signs in automatically via its WP OAuth flow.
 *
 * Every open re-mints the login URL, so WP cookie lifetime in the WebView
 * never matters. Membership is enforced server-side by WordPress (same gate
 * as the web community page); `not_member` maps to the native paywall so the
 * WebView can never land on the join/checkout page (App Store anti-steering).
 */
export const CommunityWebViewModal = ({ path, onClose }: CommunityWebViewModalProps) => {
  const insets = useSafeAreaInsets();
  const router = useRouter();
  const { preferenceMetadata } = useAuth();
  // Route to navigate to AFTER the modal has fully dismissed. Pushing while
  // the native modal is still animating out gets silently dropped by iOS
  // (seen in prod: the Hang's pro-upgrade tap "did nothing" and revealed a
  // stale screen underneath). onDismiss is the safe moment to navigate; the
  // timeout is a belt-and-braces fallback since onDismiss is iOS-only.
  const pendingRouteRef = useRef<string | null>(null);
  const navigateAfterDismiss = useCallback(
    (route: string) => {
      pendingRouteRef.current = route;
      onClose();
      setTimeout(() => {
        if (pendingRouteRef.current === route) {
          pendingRouteRef.current = null;
          router.push(route as never);
        }
      }, 600);
    },
    [onClose, router],
  );
  const flushPendingRoute = useCallback(() => {
    const route = pendingRouteRef.current;
    if (route) {
      pendingRouteRef.current = null;
      router.push(route as never);
    }
  }, [router]);

  // Links into the app's content (posted in Circle content or event pages)
  // stay in the app: the full-screen modal covers every native screen, so
  // close The Hang and push the route once the dismiss has finished.
  //  - app.openstudiojazz.com/… → that route, directly.
  //  - www.openstudiojazz.com/courses/<course>[/<lesson>] (LearnDash
  //    permalinks, same shape as the app's routes) → resolved against
  //    Supabase: published or redirect-type course → native screen; WP-only
  //    (draft) course or lookup failure → browser, logged in (today's path,
  //    where WordPress serves the page or redirects to the web app itself).
  // Returns false when the URL is neither, so the caller keeps going.
  const handleAppLink = useCallback(
    (url: string): boolean => {
      const route = appRouteForAbsoluteUrl(url);
      if (route) {
        navigateAfterDismiss(route);
        return true;
      }
      const wpLink = parseWpCourseUrl(url);
      if (!wpLink) return false;
      void (async () => {
        const wpRoute = await resolveWpCourseRoute(wpLink, {
          getCourseBySlug,
          publishedLessonExists,
        });
        if (wpRoute) {
          navigateAfterDismiss(wpRoute);
        } else {
          void openWpUrlInBrowserLoggedIn(url, wpLink.pathname);
        }
      })();
      return true;
    },
    [navigateAfterDismiss],
  );
  const [phase, setPhase] = useState<Phase>('loading');
  const [loginUrl, setLoginUrl] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const visible = !!path;

  const load = useCallback(async (nextPath: string) => {
    setPhase('loading');
    setLoginUrl(null);
    const result = await fetchCommunityAutologinUrl(nextPath);
    if (result.status === 'ok') {
      setLoginUrl(result.loginUrl);
      setPhase('ready');
    } else if (result.status === 'not_member') {
      setPhase('not_member');
    } else {
      setErrorMessage(result.message);
      setPhase('error');
    }
  }, []);

  useEffect(() => {
    if (path) {
      void load(path);
    }
  }, [path, load]);

  const showSignInError = useCallback(() => {
    setErrorMessage("We couldn't sign you in to The Hang. Please try again.");
    setPhase('error');
  }, []);

  // Pro-upgrade entry paths: /go-pro (stable alias used by the os-sessions
  // CTA) and /switch-subscription (WooCommerce's switch entry, used by pro
  // CTAs authored inside Circle content). Routed by billing:
  //  - device owns the Apple sub → native Apple upgrade (same subscription
  //    group, so Apple replaces the plan with proration — no double charge)
  //  - flagged Apple-billed but sub lives on another Apple ID → Membership
  //    screen, whose Apple panel offers Restore
  //  - web-billed → browser, logged in, to the ORIGINAL url (for
  //    /switch-subscription that is the real prorated Woo switch flow).
  // Last Circle section the user visited, mapped to its /community/... path
  // (Circle runs in an iframe, so the top-frame URL never changes). Used to
  // return the user to the exact section after a successful Pro upgrade.
  const lastCommunityPathRef = useRef<string | null>(null);

  const handleProUpgrade = useCallback(
    async (url: string, pathname: string) => {
      const info = await getCustomerInfo();
      if (hasActiveEntitlement(info)) {
        const returnPath = lastCommunityPathRef.current ?? path;
        const suffix = returnPath ? `&returnTo=${encodeURIComponent(returnPath)}` : '';
        navigateAfterDismiss(`/upgrade?mode=pro${suffix}`);
        return;
      }
      if (readMembershipSource(preferenceMetadata) === 'apple') {
        navigateAfterDismiss('/membership');
        return;
      }
      void openWpUrlInBrowserLoggedIn(url, pathname);
    },
    [navigateAfterDismiss, path, preferenceMetadata],
  );

  const handleShouldStartLoad = useCallback(
    (request: { url: string; isTopFrame?: boolean }) => {
      // Sub-frames (the Circle iframe + its OAuth hops) load freely — except
      // a pro-upgrade navigation targeting the iframe itself, which must be
      // intercepted like any other, not rendered inside Circle's frame.
      if (request.isTopFrame === false) {
        // A lesson/course link navigating the Circle iframe itself must open
        // natively, never render the web app inside Circle's frame.
        if (handleAppLink(request.url)) return false;
        try {
          const sub = new URL(request.url);
          const subHost = sub.hostname.toLowerCase();
          // Remember which Circle section the user is in (for post-upgrade
          // return). Charset-guarded so it always satisfies the autologin
          // allowlist when replayed as /community/<path>.
          if (
            (subHost === 'pro.openstudiojazz.com' || subHost.endsWith('.circle.so')) &&
            /^\/c\/[A-Za-z0-9\-_/]*$/.test(sub.pathname)
          ) {
            lastCommunityPathRef.current = `/community${sub.pathname}`;
          }
          if (
            (subHost === wpHost() ||
              subHost === 'www.openstudiojazz.com' ||
              subHost === 'openstudiojazz.com') &&
            PRO_UPGRADE_PATH_RE.test(sub.pathname)
          ) {
            void handleProUpgrade(request.url, sub.pathname);
            return false;
          }
        } catch {
          // Not a parseable URL — let the frame handle it.
        }
        return true;
      }

      const url = request.url;
      if (url === 'about:blank' || url.startsWith('about:')) return true;
      if (!/^https?:\/\//i.test(url)) {
        // mailto:, tel:, custom schemes — hand off to the OS.
        void Linking.openURL(url).catch(() => {});
        return false;
      }

      // App links (app.openstudiojazz.com) → native screen, not Safari.
      if (handleAppLink(url)) return false;

      const host = hostnameOf(url);
      const isWp =
        host === wpHost() || host === 'www.openstudiojazz.com' || host === 'openstudiojazz.com';

      if (isWp) {
        let pathname = '';
        try {
          pathname = new URL(url).pathname;
        } catch {
          return false;
        }
        // The autologin round-trip (home_url + ?osj_autologin=) and everything
        // under /community and the events area (/os-sessions + /events
        // calendar) stay in.
        if (
          url.includes('osj_autologin=') ||
          /^\/(community|os-sessions|events)(\/|$)/.test(pathname)
        ) {
          return true;
        }
        // Pro upgrade CTA: route by billing (native Apple upgrade vs web).
        if (PRO_UPGRADE_PATH_RE.test(pathname)) {
          void handleProUpgrade(url, pathname);
          return false;
        }
        // Token failed → WP bounces to my-account/wp-login. Surface a native
        // error instead of showing a logged-out WP page (or worse, site nav
        // with checkout links) inside the app.
        if (/^\/(my-account|wp-login)/.test(pathname)) {
          logger.warn('[CommunityWebView] Autologin bounced to', pathname);
          showSignInError();
          return false;
        }
        // Any other WP page (user tapped a link out of the community shell,
        // e.g. an upgrade-to-Pro CTA): default browser, logged in via a
        // one-time autologin URL so checkout flows are seamless. Falls back
        // to the plain URL if minting fails.
        void openWpUrlInBrowserLoggedIn(url, pathname);
        return false;
      }

      if (isAllowedTopFrameHost(host)) return true;

      // External content (user-posted links etc.) → Safari.
      void Linking.openURL(url).catch(() => {});
      return false;
    },
    [handleAppLink, handleProUpgrade, showSignInError],
  );

  if (Platform.OS === 'web') return null;

  // Events pages (/os-sessions, /events) share this WebView with The Hang.
  const isHang = !path || /^\/community(\/|$)/.test(path);
  const headerTitle = isHang ? 'The Hang' : 'Live Sessions';

  const header = (
    <XStack
      ai="center"
      jc="space-between"
      px="$3"
      py="$2"
      backgroundColor={HEADER_BG}
      borderBottomWidth={1}
      borderBottomColor="#26242B"
    >
      <CardTitle fontWeight="600" color="white" numberOfLines={1} flex={1} pr="$3">
        {headerTitle}
      </CardTitle>
      <TouchableOpacity
        onPress={onClose}
        style={{
          width: 36,
          height: 36,
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 18,
        }}
        accessibilityLabel="Close The Hang"
      >
        <Ionicons name="close" size={26} color="white" />
      </TouchableOpacity>
    </XStack>
  );

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="fullScreen"
      onRequestClose={onClose}
      onDismiss={flushPendingRoute}
    >
      <View style={{ flex: 1, backgroundColor: HEADER_BG, paddingTop: insets.top }}>
        {header}
        <View style={{ flex: 1, backgroundColor: BG }}>
          {phase === 'ready' && loginUrl && (
            <WebView
              source={{ uri: loginUrl }}
              style={{ flex: 1, backgroundColor: BG }}
              originWhitelist={['https://*']}
              injectedJavaScriptBeforeContentLoaded={HIDE_WP_CHROME_JS}
              startInLoadingState
              sharedCookiesEnabled
              allowsInlineMediaPlayback
              allowsBackForwardNavigationGestures
              onShouldStartLoadWithRequest={handleShouldStartLoad}
              onOpenWindow={(event) => {
                // window.open / target=_blank. App links → native screen;
                // openstudiojazz.com targets (e.g. event links posted inside
                // Circle) → browser, logged in via autologin; everything
                // else → browser, plain.
                const targetUrl = event.nativeEvent.targetUrl;
                if (!targetUrl) return;
                // Circle opens external links in a new window: lesson/course
                // links to the app itself open natively instead of Safari.
                if (handleAppLink(targetUrl)) return;
                const targetHost = hostnameOf(targetUrl);
                const isWpTarget =
                  targetHost === wpHost() ||
                  targetHost === 'www.openstudiojazz.com' ||
                  targetHost === 'openstudiojazz.com';
                if (isWpTarget) {
                  let targetPath = '/';
                  try {
                    targetPath = new URL(targetUrl).pathname || '/';
                  } catch {
                    // keep '/'
                  }
                  if (PRO_UPGRADE_PATH_RE.test(targetPath)) {
                    void handleProUpgrade(targetUrl, targetPath);
                  } else {
                    void openWpUrlInBrowserLoggedIn(targetUrl, targetPath);
                  }
                } else {
                  void Linking.openURL(targetUrl).catch(() => {});
                }
              }}
              onError={(event) => {
                logger.error('[CommunityWebView] Load error:', event.nativeEvent.description);
                setErrorMessage('Could not load The Hang. Please try again.');
                setPhase('error');
              }}
              renderLoading={() => (
                <View
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: BG,
                  }}
                >
                  <ActivityIndicator size="large" color={LOADING_SPINNER_COLOR} />
                </View>
              )}
            />
          )}

          {phase === 'loading' && (
            <YStack f={1} ai="center" jc="center">
              <ActivityIndicator size="large" color={LOADING_SPINNER_COLOR} />
            </YStack>
          )}

          {phase === 'not_member' && (
            <YStack f={1} ai="center" jc="center" px="$5" gap="$4">
              <BodyText color="white" textAlign="center" fontSize={16}>
                {isHang
                  ? 'The Hang is our members-only community. Join Open Studio to be part of it.'
                  : 'Live sessions are for Open Studio members. Join to attend them live.'}
              </BodyText>
              <Pressable
                onPress={() => {
                  navigateAfterDismiss('/upgrade');
                }}
                style={({ pressed }) => ({
                  backgroundColor: pressed ? '#d8551f' : '#F2663A',
                  borderRadius: 8,
                  paddingVertical: 12,
                  paddingHorizontal: 24,
                })}
              >
                <BodyText color="white" fontWeight="600">
                  View Membership Options
                </BodyText>
              </Pressable>
            </YStack>
          )}

          {phase === 'error' && (
            <YStack f={1} ai="center" jc="center" px="$5" gap="$4">
              <BodyText color="white" textAlign="center" fontSize={16}>
                {errorMessage || 'Something went wrong.'}
              </BodyText>
              <Pressable
                onPress={() => path && void load(path)}
                style={({ pressed }) => ({
                  backgroundColor: pressed ? '#d8551f' : '#F2663A',
                  borderRadius: 8,
                  paddingVertical: 12,
                  paddingHorizontal: 24,
                })}
              >
                <BodyText color="white" fontWeight="600">
                  Try Again
                </BodyText>
              </Pressable>
            </YStack>
          )}
        </View>
      </View>
    </Modal>
  );
};
